import React, { useState, useRef } from 'react';

/**
 * Grafico de barras para analise operacional.
 * Suporta layout vertical (padrao) e horizontal.
 *
 * Props:
 *   data: array de objetos
 *   labelKey: chave do label (eixo Y no horizontal, eixo X no vertical)
 *   valueKey: chave do valor
 *   tooltipKeys: array de chaves para mostrar no tooltip (opcional)
 *   tooltipLabels: objeto {chave: label} para renomear campos no tooltip
 *   formatter: funcao (valor) => string
 *   height: altura do grafico em px (default 280)
 *   layout: 'vertical' | 'horizontal' (default 'vertical')
 *   emptyMessage: mensagem quando nao ha dados
 *   loading: mostra skeleton
 */
export default function GraficoBarras({
  data,
  labelKey,
  valueKey,
  tooltipKeys = [],
  tooltipLabels = {},
  formatter = (v) => v,
  height = 280,
  layout = 'vertical',
  emptyMessage = 'Sem dados',
  loading = false,
}) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const containerRef = useRef(null);

  if (loading) {
    return (
      <div style={{
        height,
        display: 'flex',
        alignItems: layout === 'horizontal' ? 'center' : 'flex-end',
        gap: '6px',
        padding: '0 4px',
      }}>
        {Array.from({ length: 15 }).map((_, i) => (
          <div key={i} style={{
            flex: 1,
            height: layout === 'horizontal' ? '60%' : `${15 + Math.random() * 65}%`,
            background: 'var(--border)',
            borderRadius: '3px 3px 0 0',
            opacity: 0.35,
          }} />
        ))}
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div style={{
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--dim)',
        fontSize: 'var(--font-sm)',
      }}>
        {emptyMessage}
      </div>
    );
  }

  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  const totalBars = data.length;

  // Formata tooltip completo em PT-BR (para datas)
  const formatTooltip = (dateStr) => {
    if (!dateStr) return '';
    const mesesPT = { jan: 'Jan', fev: 'Fev', mar: 'Mar', abr: 'Abr', mai: 'Mai', jun: 'Jun',
                      jul: 'Jul', ago: 'Ago', set: 'Set', out: 'Out', nov: 'Nov', dez: 'Dez' };
    const diasPT = { mon: 'Seg', tue: 'Ter', wed: 'Qua', thu: 'Qui', fri: 'Sex', sat: 'Sab', sun: 'Dom' };

    const partes = String(dateStr).split('-');
    if (partes.length === 3) {
      const dia = partes[2];
      const mes = partes[1];
      const dateObj = new Date(`${dateStr}T12:00:00`);
      const diaEn = dateObj.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
      const diaPT = diasPT[diaEn] || diaEn;
      return `${dia}/${mes} - ${diaPT}`;
    }
    const dia = partes[0];
    const abrev = partes[1]?.toLowerCase() || '';
    const diaPT = diasPT[abrev];
    if (diaPT) {
      return `${dia} - ${diaPT}`;
    }
    const mesPT = mesesPT[abrev] || abrev;
    return `${dia}/${mesPT}`;
  };

  // Renderiza tooltip content
  const renderTooltip = (d) => {
    const label = d[labelKey];
    const value = d[valueKey];
    return (
      <>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>
          {typeof label === 'string' && label.includes('-') ? formatTooltip(label) : `${label}`}
        </div>
        <div>US: <span style={{ fontWeight: 600, color: 'var(--amber)' }}>{formatter(value)}</span></div>
        {tooltipKeys.map(key => {
          const val = d[key];
          if (val == null) return null;
          const labelName = tooltipLabels[key] || key;
          return (
            <div key={key}>{labelName}: <span style={{ fontWeight: 600 }}>{typeof val === 'number' && key.includes('media') ? val.toFixed(2) : val}</span></div>
          );
        })}
      </>
    );
  };

  // ==================== LAYOUT HORIZONTAL ====================
  if (layout === 'horizontal') {
    const barHeight = Math.max(Math.min(20, (height - 20) / totalBars), 8);
    const gap = 2;
    const labelWidth = 60;
    const valueWidth = 55;
    const chartWidth = `calc(100% - ${labelWidth + valueWidth + 10}px)`;
    const totalHeight = totalBars * (barHeight + gap);

    return (
      <div ref={{ current: null }} style={{ width: '100%', position: 'relative', overflowY: 'auto', maxHeight: `${height}px` }}>
        {/* Tooltip */}
        {hoveredIdx !== null && data[hoveredIdx] && (
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            fontSize: 'var(--font-xs)',
            color: 'var(--text)',
            whiteSpace: 'nowrap',
            zIndex: 100,
            boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
            pointerEvents: 'none',
            lineHeight: 1.8,
          }}>
            {renderTooltip(data[hoveredIdx])}
          </div>
        )}

        <div style={{ minHeight: `${totalHeight}px`, position: 'relative' }}>
          {data.map((d, i) => {
            const val = d[valueKey] || 0;
            const barW = max > 0 ? (val / max) * 100 : 0;
            const isHovered = hoveredIdx === i;
            const isZero = val === 0;

            return (
              <div
                key={i}
                onMouseEnter={() => setHoveredIdx(i)}
                onMouseLeave={() => setHoveredIdx(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  height: `${barHeight}px`,
                  marginBottom: `${gap}px`,
                  cursor: 'pointer',
                }}
              >
                {/* Label (numvei) */}
                <div style={{
                  width: `${labelWidth}px`,
                  textAlign: 'right',
                  fontSize: 'var(--font-xs)',
                  fontWeight: isHovered ? 700 : 500,
                  color: isHovered ? 'var(--amber)' : 'var(--text)',
                  fontVariantNumeric: 'tabular-nums',
                  flexShrink: 0,
                }}>
                  {d[labelKey]}
                </div>

                {/* Barra */}
                <div style={{
                  flex: 1,
                  height: '100%',
                  position: 'relative',
                  background: 'var(--surface)',
                  borderRadius: '2px',
                }}>
                  <div style={{
                    width: `${barW}%`,
                    height: '100%',
                    background: isHovered ? 'var(--amber-l)' : (isZero ? 'var(--dim)' : 'var(--amber)'),
                    borderRadius: '2px',
                    opacity: hoveredIdx === null || isHovered ? 1 : 0.5,
                    transition: 'width 0.15s ease',
                    minWidth: isZero ? '0' : '2px',
                  }} />
                </div>

                {/* Valor */}
                <div style={{
                  width: `${valueWidth}px`,
                  textAlign: 'right',
                  fontSize: 'var(--font-xs)',
                  fontWeight: 600,
                  color: 'var(--amber)',
                  fontVariantNumeric: 'tabular-nums',
                  flexShrink: 0,
                }}>
                  {formatter(val)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ==================== LAYOUT VERTICAL (original) ====================
  const barAreaTop = 10;
  const barAreaBottom = 38;
  const chartH = height - barAreaTop - barAreaBottom;

  const formatDayMonth = (dateStr) => {
    if (!dateStr) return '';
    const partes = String(dateStr).split('-');
    if (partes.length === 3) {
      return `${partes[2]}/${partes[1]}`;
    }
    return partes[0] || dateStr;
  };

  return (
    <div ref={containerRef} style={{ width: '100%', position: 'relative' }}>
      {/* Tooltip */}
      {hoveredIdx !== null && data[hoveredIdx] && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--surface-2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          padding: '8px 14px',
          fontSize: 'var(--font-xs)',
          color: 'var(--text)',
          whiteSpace: 'nowrap',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
          pointerEvents: 'none',
          lineHeight: 1.6,
        }}>
          {renderTooltip(data[hoveredIdx])}
        </div>
      )}

      <svg width="100%" height={height} style={{ display: 'block' }}>
        <line x1={4} y1={barAreaTop + chartH} x2="99.5%" y2={barAreaTop + chartH} stroke="var(--border)" strokeWidth={0.5} />

        {data.map((d, i) => {
          const val = d[valueKey] || 0;
          const barH = max > 0 ? (val / max) * chartH : 0;
          const barW = 100 / totalBars;
          const xPct = i * barW;
          const isHovered = hoveredIdx === i;
          const isZero = val === 0;

          return (
            <g key={i} onMouseEnter={() => setHoveredIdx(i)} onMouseLeave={() => setHoveredIdx(null)} style={{ cursor: 'pointer' }}>
              <rect x={`${xPct}%`} y={barAreaTop} width={`${barW}%`} height={chartH} fill="transparent" />
              <rect
                x={`${xPct + 0.3}%`}
                y={barAreaTop + chartH - barH}
                width={`${barW - 0.6}%`}
                height={Math.max(barH, 1)}
                fill={isHovered ? 'var(--amber-l)' : (isZero ? 'var(--dim)' : 'var(--amber)')}
                rx={3}
                opacity={hoveredIdx === null || isHovered ? 1 : 0.5}
              />
              <text
                x={`${xPct + barW / 2}%`}
                y={barAreaTop + chartH + 14}
                textAnchor="middle"
                fontSize={9}
                fill={isHovered ? 'var(--amber)' : 'var(--muted)'}
                fontWeight={isHovered ? 700 : 400}
              >
                {formatDayMonth(d[labelKey] || '')}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
